import React, { useEffect, useRef } from 'react';
import { CheckCircle, Clock, Shield, MapPin, Calendar, UserCheck, Smartphone } from 'lucide-react';
import Container from '../ui/Container';

interface FeatureProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  delay: number;
}

const Feature = ({ icon, title, description, delay }: FeatureProps) => {
  const featureRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    const feature = featureRef.current;
    
    if (feature) {
      feature.style.opacity = '0';
      feature.style.transform = 'translateY(20px)';
      
      setTimeout(() => {
        feature.style.transition = 'opacity 0.6s ease, transform 0.6s ease';
        feature.style.opacity = '1';
        feature.style.transform = 'translateY(0)';
      }, delay);
    }
  }, [delay]);

  return (
    <div ref={featureRef} className="flex flex-col items-center text-center p-6">
      <div className="bg-orange-100 p-4 rounded-full text-orange-600 mb-4">
        {icon}
      </div>
      <h3 className="text-xl font-bold mb-2">{title}</h3>
      <p className="text-gray-600">{description}</p>
    </div>
  );
};

const PlatformFeatures = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const features = [
    {
      icon: <Clock size={28} />,
      title: '24/7 Availability',
      description: 'Book professional drivers any time of day or night, even on short notice.',
      delay: 100
    },
    {
      icon: <Shield size={28} />,
      title: 'Verified Professionals',
      description: 'All drivers undergo thorough background checks and verification processes.',
      delay: 200
    },
    {
      icon: <MapPin size={28} />,
      title: 'Real-Time Tracking',
      description: 'Track your driver\'s location in real-time for precise pickup timing.',
      delay: 300
    },
    {
      icon: <Calendar size={28} />,
      title: 'Flexible Scheduling',
      description: 'Book drivers for hourly, daily, or weekly durations based on your needs.',
      delay: 400
    },
    {
      icon: <UserCheck size={28} />,
      title: 'Driver Profiles',
      description: 'View detailed driver profiles including ratings, experience, and specialties.',
      delay: 500
    },
    {
      icon: <Smartphone size={28} />,
      title: 'Mobile App',
      description: 'Manage bookings, track drivers, and make payments from our convenient mobile app.',
      delay: 600
    }
  ];

  return (
    <section className="py-16 md:py-24">
      <Container>
        <div ref={sectionRef} className="opacity-0 transition-opacity duration-1000">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Platform Features</h2>
            <div className="w-20 h-1 bg-orange-500 mx-auto mb-6"></div>
            <p className="text-gray-600 max-w-2xl mx-auto">
              DriversOnCall provides a comprehensive solution for all your professional driver needs, with features designed for convenience, safety, and reliability.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Feature
                key={index}
                icon={feature.icon}
                title={feature.title}
                description={feature.description}
                delay={feature.delay}
              />
            ))}
          </div>
          
          <div className="mt-16 bg-blue-50 rounded-lg p-8 border border-blue-100">
            <h3 className="text-2xl font-bold text-blue-900 mb-4">Why Choose DriversOnCall?</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-start">
                <CheckCircle className="text-green-500 mr-3 flex-shrink-0 mt-1" size={20} />
                <p className="text-gray-700">Professional, experienced drivers with clean records</p>
              </div>
              <div className="flex items-start">
                <CheckCircle className="text-green-500 mr-3 flex-shrink-0 mt-1" size={20} />
                <p className="text-gray-700">Transparent pricing with no hidden fees</p>
              </div>
              <div className="flex items-start">
                <CheckCircle className="text-green-500 mr-3 flex-shrink-0 mt-1" size={20} />
                <p className="text-gray-700">Secure payment processing through the platform</p>
              </div>
              <div className="flex items-start">
                <CheckCircle className="text-green-500 mr-3 flex-shrink-0 mt-1" size={20} />
                <p className="text-gray-700">24/7 customer support for assistance</p>
              </div>
              <div className="flex items-start">
                <CheckCircle className="text-green-500 mr-3 flex-shrink-0 mt-1" size={20} />
                <p className="text-gray-700">Insurance coverage for added peace of mind</p>
              </div>
              <div className="flex items-start">
                <CheckCircle className="text-green-500 mr-3 flex-shrink-0 mt-1" size={20} />
                <p className="text-gray-700">Rating system to ensure quality service</p>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
};

export default PlatformFeatures;